// src/components/sections/ProjectsSection.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { fadeIn, scaleIn } from '../../styles/animations';
import SectionTitle from '../ui/SectionTitle';
import { realizationsService } from '../../services/api';

const ProjectsWrapper = styled.section`
  background-color: ${props => props.theme.colors.gray};
  padding: ${props => props.theme.spacing.xl} 0;
`;

const ProjectsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const ProjectsIntro = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const FilterButtons = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
  margin: ${props => props.theme.spacing.lg} 0;
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: 0.2s;
  opacity: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    gap: ${props => props.theme.spacing.xs};
  }
`;

const FilterButton = styled.button`
  background-color: ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? props.theme.colors.light : props.theme.colors.textMain};
  border: 1px solid ${props => props.active ? props.theme.colors.primary : '#ddd'};
  padding: 0.5rem 1rem;
  border-radius: ${props => props.theme.borderRadius.small};
  font-weight: 500;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : '#f0f0f0'};
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
  transition: all ${props => props.theme.transitions.medium};
  animation: ${scaleIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.medium};
    
    .project-image img {
      transform: scale(1.05);
    }
  }
`;

const ProjectImage = styled.div`
  height: 240px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${props => props.theme.transitions.medium};
  }
`;

const ProjectContent = styled.div`
  padding: ${props => props.theme.spacing.md};
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ProjectTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 0;
`;

const ProjectLocation = styled.span`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const ProjectLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  margin-top: ${props => props.theme.spacing.sm};
  transition: all ${props => props.theme.transitions.fast};
  
  svg {
    margin-left: 0.5rem;
    transition: transform ${props => props.theme.transitions.fast};
  }
  
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    
    svg {
      transform: translateX(5px);
    }
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.light};
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  margin-top: ${props => props.theme.spacing.lg};
  transition: background-color ${props => props.theme.transitions.fast};
  text-decoration: none;
  
  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
    color: ${props => props.theme.colors.light};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${props => props.theme.spacing.lg};
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: 0.6s;
  opacity: 0;
`;

const ProjectsSection = () => {
    const [filter, setFilter] = useState('all');
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        // realizationsService.getAll() returns { realizations: [...], pagination: {...} }
        realizationsService.getAll()
            .then(data => {
                // data is { realizations: [...], pagination: {...} }
                const raw = Array.isArray(data.realizations) ? data.realizations : [];
                setProjects(raw);
            })
            .catch(() => setProjects([]));
    }, []);

    const categories = [
        { id: 'all', name: 'Tous' },
        { id: 'terrasses-en-bois', name: 'Terrasses en bois' },
        { id: 'terrasses-en-bois-composite', name: 'Composite' },
        { id: 'plages-de-piscine-en-bois', name: 'Piscines' },
        { id: 'balcons-terrasses-appartement', name: 'Balcons' },
        { id: 'amenagements-exterieurs', name: 'Aménagements' },
        { id: 'terrasse-en-ceramique-sur-plots-ou-structure-metallique', name: 'Céramique' },
    ];

    const filteredProjects = (filter === 'all'
        ? projects
        : projects.filter(project => project.category_slug === filter)).slice(0, 6);

    return (
        <ProjectsWrapper id="realisations">
            <ProjectsContainer>
                <ProjectsIntro>
                    <SectionTitle
                        text="Nos réalisations"
                        subtitle="Découvrez quelques exemples de nos créations personnalisées qui embellissent les espaces extérieurs de nos clients."
                        centered
                    />
                </ProjectsIntro>

                <FilterButtons>
                    {categories.map(category => (
                        <FilterButton
                            key={category.id}
                            active={filter === category.id}
                            onClick={() => setFilter(category.id)}
                        >
                            {category.name}
                        </FilterButton>
                    ))}
                </FilterButtons>

                <ProjectsGrid>
                    {filteredProjects.map((project, index) => (
                        <ProjectCard key={project.id} delay={`${0.1 * index}s`}>
                            <ProjectImage className="project-image">
                                <img src={project.featured_image ? `https://decko.fr/uploads/${project.featured_image}` : ''} alt={project.title} />
                            </ProjectImage>
                            <ProjectContent>
                                <ProjectHeader>
                                    <ProjectTitle>{project.title}</ProjectTitle>
                                    <ProjectLocation>{project.location}</ProjectLocation>
                                </ProjectHeader>
                                <ProjectLink to={`/realisations/${project.id}`}>
                                    Voir le projet
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z" />
                                    </svg>
                                </ProjectLink>
                            </ProjectContent>
                        </ProjectCard>
                    ))}
                </ProjectsGrid>

                <ButtonContainer>
                    <CTAButton to="/realisations">
                        Voir toutes nos réalisations
                    </CTAButton>
                </ButtonContainer>
            </ProjectsContainer>
        </ProjectsWrapper>
    );
};

export default ProjectsSection;